import React, { useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import AuthService from '../services/auth.service';

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;
const GSI_SRC = 'https://accounts.google.com/gsi/client';

let gsiPromise = null;
const loadGsi = () => {
  if (window.google && window.google.accounts && window.google.accounts.id) return Promise.resolve();
  if (gsiPromise) return gsiPromise;
  gsiPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = GSI_SRC;
    script.async = true;
    script.defer = true;
    script.onload = resolve;
    script.onerror = () => {
      gsiPromise = null;
      reject(new Error('Failed to load Google sign-in'));
    };
    document.head.appendChild(script);
  });
  return gsiPromise;
};

/**
 * "Sign in with Google" / "Sign up with Google" button.
 * mode: 'signin' | 'signup' (only changes the button label).
 */
const GoogleAuthButton = ({ mode = 'signin' }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const btnRef = useRef(null);

  useEffect(() => {
    if (!GOOGLE_CLIENT_ID) return;
    let cancelled = false;

    const handleCredential = (response) => {
      AuthService.googleLogin(response.credential).then(
        () => {
          navigate('/profile');
          window.location.reload();
        },
        (error) => {
          const msg = (error.response && error.response.data && error.response.data.message) || t('auth.googleFailed');
          toast.error(msg);
        },
      );
    };

    loadGsi()
      .then(() => {
        if (cancelled || !btnRef.current) return;
        window.google.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: handleCredential,
        });
        btnRef.current.innerHTML = '';
        window.google.accounts.id.renderButton(btnRef.current, {
          type: 'standard',
          theme: 'outline',
          size: 'large',
          shape: 'rectangular',
          text: mode === 'signup' ? 'signup_with' : 'signin_with',
          locale: i18n.language,
          width: 300,
        });
      })
      .catch(() => !cancelled && toast.error(t('auth.googleFailed')));

    return () => {
      cancelled = true;
    };
  }, [mode, i18n.language, navigate, t]);

  if (!GOOGLE_CLIENT_ID) {
    return <div className='text-muted small text-center'>{t('auth.googleNotConfigured')}</div>;
  }

  return <div ref={btnRef} className='d-flex justify-content-center' />;
};

export const AuthDivider = () => {
  const { t } = useTranslation();
  return (
    <div className='d-flex align-items-center my-3'>
      <hr className='flex-grow-1' />
      <span className='mx-2 text-muted small'>{t('auth.orContinueWith')}</span>
      <hr className='flex-grow-1' />
    </div>
  );
};

export default GoogleAuthButton;
